import { useState, useEffect, useRef, useMemo } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { useConfig } from "../context/useConfig";
import { fadeInUp } from "../utils/motionVariants";

// Central list of section IDs expected in the DOM. Update here if sections change.
const SECTION_IDS = ["about", "experience", "projects", "skills", "hobbies", "contact"]; 

// Duration (ms) of the mobile menu open/close height animation.
const MOBILE_MENU_ANIMATION_MS = 300; // keep in sync with AnimatePresence transition (duration: 0.3)

// Helper: returns a safe numeric value (fallback if NaN)
const safeNumber = (n, fallback = 0) => (Number.isFinite(n) ? n : fallback);

function Navbar() {
  const { config } = useConfig();
  const [activeSection, setActiveSection] = useState("");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false); // track scroll to adjust styles
  const [isInitialMount, setIsInitialMount] = useState(true); // prevent color flash on mount
  const navRef = useRef(null); // reference to <nav> for measuring height & outside click
  const toggleBtnRef = useRef(null); // reference to toggle button to avoid accidental close

  // Compute current dynamic offset (navbar height + small breathing room).
  const getNavOffset = () => {
    // We only want the *collapsed* navbar height for scroll offset; if mobile menu is open,
    // measuring navRef would include the expanded panel. So instead, if open, approximate using
    // the first child container height (brand + toggles) which stays constant.
    if (isMobileMenuOpen && navRef.current) {
      const firstRow = navRef.current.querySelector(':scope > div');
      const rowHeight = firstRow?.offsetHeight || 0;
      return safeNumber(rowHeight + 12, 96);
    }
    const h = navRef.current?.offsetHeight || 0;
    return safeNumber(h + 12, 96); // default ~96 if cannot measure
  };

  // Disable initial mount flag after first paint to enable smooth transitions
  useEffect(() => {
    // Wait for initial layout and section detection to complete
    const timer = setTimeout(() => {
      setIsInitialMount(false);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Track scroll for active section & navbar styling
  useEffect(() => {
    let ticking = false; // rudimentary throttle using requestAnimationFrame
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const offset = getNavOffset();
          const scrollPosition = window.scrollY + offset;

          // Default to 'home' if at top or no section matches
          let foundSection = null;

          // Check if we're at the very top (home)
          if (window.scrollY < 100) {
            foundSection = '';
          } else {
            // Find the current section
            for (const id of SECTION_IDS) {
              const el = document.getElementById(id);
              if (el) {
                const { offsetTop, offsetHeight } = el;
                if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
                  foundSection = id;
                  break;
                }
              }
            }
          }

          // Only update if section actually changed to prevent unnecessary re-renders
          if (foundSection !== null) {
            setActiveSection((prev) => prev !== foundSection ? foundSection : prev);
          }

          setScrolled(window.scrollY > 80);
          ticking = false;
        });
        ticking = true;
      }
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isMobileMenuOpen]); // Add isMobileMenuOpen to deps since getNavOffset depends on it

  // Close mobile menu on window resize to desktop size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768 && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobileMenuOpen]);

  // Close mobile menu on escape key & outside click
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") setIsMobileMenuOpen(false);
    };
    const handleClickOutside = (e) => {
      if (!isMobileMenuOpen) return;
      if (toggleBtnRef.current?.contains(e.target)) return; // allow toggle button
      if (navRef.current && !navRef.current.contains(e.target)) {
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener("keydown", handleKey);
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  // Labels now optionally sourced from config.ui.nav or fallback list for 100% config-driven text.
  const navConfig = config.ui?.nav || {};
  const navLinks = [
    { href: "#about", label: navConfig.about || "About" },
    { href: "#experience", label: navConfig.experience || "Experience" },
    { href: "#projects", label: navConfig.projects || "Projects" },
    { href: "#skills", label: navConfig.skills || "Skills" },
    { href: "#hobbies", label: navConfig.hobbies || "Hobbies" },
    { href: "#contact", label: navConfig.contact || "Contact" },
  ];

  const reduceMotion = useReducedMotion();

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Close mobile menu when clicking on a link and scroll smoothly
  const performScrollTo = (targetId) => {
    if (targetId === 'home' || targetId === '') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    const element = document.getElementById(targetId);
    if (element) {
      const navOffset = getNavOffset();
      const elementTop = element.getBoundingClientRect().top + window.pageYOffset;
      const targetScroll = elementTop - navOffset;
      window.scrollTo({ top: targetScroll, behavior: 'smooth' });
      // Optionally update hash without jumping (history API) for shareable URL.
      if (history.replaceState) {
        history.replaceState(null, '', `#${targetId}`);
      }
    } else {
      window.location.hash = targetId; // fallback
    }
  };

  const handleLinkClick = (e, href) => {
    e.preventDefault();
    const targetId = href.replace('#', '');
    setActiveSection(targetId === '' ? 'home' : targetId);

    // If mobile menu is open, close first and delay scroll until after collapse animation
    if (isMobileMenuOpen) {
      closeMobileMenu();
      // Use two rAFs for safety OR simple timeout matching animation.
      setTimeout(() => {
        // Extra rAF ensures layout is stable post transition.
        requestAnimationFrame(() => performScrollTo(targetId));
      }, MOBILE_MENU_ANIMATION_MS + 10);
    } else {
      performScrollTo(targetId);
    }
  };

  // Dynamic theming map per section (light mode focus). Could expand using CSS vars.
  const themeMap = useMemo(() => ({
    '': {
      bg: "bg-white/70 supports-[backdrop-filter]:bg-white/55", 
      accentText: "gradient-text",
      linkActive: "text-emerald-600",
      indicator: "from-emerald-400 via-cyan-400 to-emerald-500"
    },
    home: {
      bg: "bg-white/70 supports-[backdrop-filter]:bg-white/55", 
      accentText: "gradient-text",
      linkActive: "text-emerald-600",
      indicator: "from-emerald-400 via-cyan-400 to-emerald-500"
    },
    about: {
      bg: "bg-emerald-50/70 supports-[backdrop-filter]:bg-emerald-50/50",
      accentText: "gradient-text",
      linkActive: "text-emerald-600",
      indicator: "from-emerald-400 via-cyan-400 to-emerald-500"
    },
    experience: {
      bg: "bg-cyan-50/70 supports-[backdrop-filter]:bg-cyan-50/50",
      accentText: "gradient-text",
      linkActive: "text-cyan-600",
      indicator: "from-cyan-400 via-emerald-400 to-cyan-500"
    },
    projects: {
      bg: "bg-white/70 supports-[backdrop-filter]:bg-white/55",
      accentText: "gradient-text",
      linkActive: "text-emerald-600",
      indicator: "from-emerald-400 via-cyan-400 to-emerald-500"
    },
    skills: {
      bg: "bg-emerald-50/70 supports-[backdrop-filter]:bg-emerald-50/50",
      accentText: "gradient-text",
      linkActive: "text-emerald-600",
      indicator: "from-emerald-400 via-cyan-400 to-emerald-500"
    },
    hobbies: {
      bg: "bg-white/70 supports-[backdrop-filter]:bg-white/55",
      accentText: "gradient-text",
      linkActive: "text-emerald-600",
      indicator: "from-emerald-400 via-teal-400 to-cyan-500"
    },
    contact: {
      bg: "bg-cyan-50/70 supports-[backdrop-filter]:bg-cyan-50/50",
      accentText: "gradient-text",
      linkActive: "text-cyan-600",
      indicator: "from-cyan-400 via-emerald-400 to-cyan-500"
    }
  }), []);

  const currentTheme = themeMap[activeSection || 'home'] || themeMap.home;

  return (
    <nav
      ref={navRef}
      aria-label={navConfig.primaryLabel || "Primary"}
      className={`sticky top-0 z-50 w-full ${isInitialMount ? '' : 'transition-colors duration-500'} backdrop-blur-xl safe-top ${
        isMobileMenuOpen
          ? "bg-gray-900/95 supports-[backdrop-filter]:bg-gray-900/85 border-gray-700 border-b"
          : scrolled
            ? `${currentTheme.bg} border-gray-200 shadow-sm border-b`
            : `${currentTheme.bg} border-gray-200 border-b`
      }`}
    >
      {/* Skip link for accessibility */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[60] rounded bg-indigo-600 px-4 py-2 text-xs font-semibold text-white shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
      >
        {config.ui?.skipToContentLabel || 'Skip to content'}
      </a>
      <div className="mx-auto max-w-7xl px-4 py-3.5 sm:py-3 flex items-center justify-between gap-4 relative">
        {/* Branding from config.site.name */}
        <motion.a
          href="#home"
          initial={{ opacity: isInitialMount ? 1 : 0, y: isInitialMount ? 0 : (reduceMotion ? 0 : -24) }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: [0.25, 0.8, 0.25, 1] }}
          className={`brand-title font-extrabold text-[1.9rem] leading-none tracking-tight select-none flex items-end gap-2 whitespace-nowrap ${
            isMobileMenuOpen
              ? "text-white"
              : `${currentTheme.accentText}`
          }`}
          onClick={(e) => {
            e.preventDefault();
            setActiveSection('');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          aria-label={config.site?.name}
        >
          <span className="-mb-[2px]">{config.site?.name || config.name}</span>
        </motion.a>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-6 lg:gap-8">
          {navLinks.map((link, i) => {
            const isActive = activeSection === link.href.substring(1);
            return (
              <motion.button
                key={link.href}
                onClick={(e) => handleLinkClick(e, link.href)}
                variants={fadeInUp(0.02 * i, reduceMotion ? 0 : 12)}
                initial={isInitialMount ? "show" : "hidden"}
                animate="show"
                className={`relative text-[11px] font-semibold tracking-[0.08em] uppercase transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 rounded px-1 py-1 ${
                  isActive ? currentTheme.linkActive : "text-gray-600 hover:text-emerald-600"
                }`}
                aria-current={isActive ? 'page' : undefined}
              >
                <span>{link.label}</span>
                {isActive && (
                  <span
                    aria-hidden
                    className={`absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r ${currentTheme.indicator} rounded-full transition-all`}
                  />
                )}
              </motion.button>
            );
          })}
        </div>

        {/* Mobile controls */}
        <div className="md:hidden flex items-center gap-3">
          <motion.button
            ref={toggleBtnRef}
            onClick={() => setIsMobileMenuOpen((o) => !o)}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-nav"
            aria-label="Toggle navigation menu"
            initial={{ x: isInitialMount ? 0 : 40, y: isInitialMount ? 0 : -40, opacity: isInitialMount ? 1 : 0 }}
            animate={{ x: 0, y: 0, opacity: 1 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.94 }}
            className={`relative font-semibold px-5 py-3.5 rounded-lg text-[10px] uppercase tracking-[0.09em] transition-all shadow-sm border border-emerald-400/50 bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500 text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-emerald-500 ${isMobileMenuOpen ? 'brightness-95' : 'hover:brightness-105'} touch-manipulation`}
          >
            {isMobileMenuOpen ? (
              <span className="flex items-center gap-2">
                {navConfig.close || 'Close'} <i className="fas fa-times text-base" />
              </span>
            ) : (
              <span className="flex items-center gap-2">
                {navConfig.menu || 'Menu'} <i className="fas fa-bars text-base" />
              </span>
            )}
          </motion.button>
        </div>
      </div>

      {/* Mobile Menu Panel */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            id="mobile-nav"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className={`md:hidden overflow-hidden border-t shadow-sm ${currentTheme.bg} border-gray-200 backdrop-blur-lg`}
          >
            <ul className="flex flex-col py-2" role="menu" aria-label={navConfig.mobileMenuLabel || 'Mobile Navigation'}>
              {navLinks.map((link, i) => {
                const isActive = activeSection === link.href.substring(1);
                return (
                  <motion.li
                    key={link.href}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.03 * i + 0.03 }}
                    role="none"
                    className="mb-1"
                  >
                    <a
                      role="menuitem"
                      href={link.href}
                      onClick={(e) => handleLinkClick(e, link.href)}
                      className={`relative block px-6 py-5 text-[13px] font-semibold tracking-wide transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/70 focus-visible:ring-offset-2 focus-visible:ring-offset-white/60 rounded-lg mx-2 touch-manipulation ${
                        isActive
                          ? `${currentTheme.linkActive} bg-white/85 shadow-inner`
                          : 'text-gray-600 hover:text-emerald-600 active:bg-white/60 hover:bg-white/40'
                      }`}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      <div className="flex items-center">
                        <span className={`w-6 h-6 flex items-center justify-center mr-3 rounded-full ${isActive ? 'bg-accent-gradient' : 'bg-gray-200'}`}>
                          <i className={`fas ${
                            link.href === '#about' ? 'fa-user' :
                            link.href === '#experience' ? 'fa-briefcase' :
                            link.href === '#projects' ? 'fa-code' :
                            link.href === '#skills' ? 'fa-code-branch' :
                            link.href === '#hobbies' ? 'fa-heart' :
                            link.href === '#contact' ? 'fa-envelope' : 'fa-link'
                          } text-xs ${isActive ? 'text-white' : 'text-gray-500'}`}></i>
                        </span>
                        {link.label}
                      </div>
                      {isActive && (
                        <span
                          aria-hidden
                          className={`absolute inset-y-0 left-0 w-1 my-1 rounded-r-full bg-gradient-to-b ${currentTheme.indicator}`}
                        />
                      )}
                    </a>
                  </motion.li>
                );
              })}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}

export default Navbar;